/**
 * Détection des feux, sans aucune clé d'API.
 *
 * La NASA publie les anomalies thermiques VIIRS et MODIS sous forme d'images
 * PNG transparentes semées de taches rouges. On les charge dans un canvas, on
 * relève les pixels rouges et on les reconvertit en coordonnées.
 *
 * Seul module de `src/data` à exiger un DOM, pour le canvas. Cela reste vrai
 * dans une coque Capacitor, qui embarque une webview complète. Toute la
 * partie calculatoire vit dans `src/core/tiles.ts`, elle, testable sans
 * navigateur.
 */
import { SCAN_DAYS_BACK, SCAN_MAX_PARALLEL, SCAN_TILE_PX } from '../core/constants';
import { isoDay } from '../core/format';
import {
  franceTileGrid,
  isFirePixel,
  pixelToLatLon,
  toBboxParam,
  type TileBox,
} from '../core/tiles';
import type { LatLon, ScanReport } from '../core/types';
import { delay, pool } from './http';

const GIBS = 'https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi';

/** Les trois couches empilées en une seule requête : deux VIIRS et MODIS. */
const FIRE_LAYERS = [
  'VIIRS_NOAA20_Thermal_Anomalies_375m_All',
  'VIIRS_SNPP_Thermal_Anomalies_375m_All',
  'MODIS_Combined_Thermal_Anomalies_All',
].join(',');

const IMAGE_TIMEOUT_MS = 15_000;
const PROBE_TIMEOUT_MS = 6_000;

/**
 * URL d'une tuile WMS.
 *
 * Construite à la main, et non avec URLSearchParams : celui-ci encoderait les
 * virgules de `layers` et de `bbox` en %2C, ce qui n'est pas garanti d'être
 * accepté par tous les serveurs WMS. Autant ne pas prendre le risque sur une
 * intégration qui fonctionne.
 */
function tileUrl(options: {
  bbox: string;
  widthPx: number;
  heightPx: number;
  day: string;
  layers?: string;
  cacheBust?: number;
}): string {
  const layers = options.layers ?? FIRE_LAYERS;
  return (
    `${GIBS}?service=WMS&request=GetMap&version=1.3.0` +
    `&layers=${layers}&styles=&format=image/png` +
    '&transparent=true&crs=EPSG:3857' +
    `&width=${options.widthPx}&height=${options.heightPx}` +
    `&bbox=${options.bbox}&time=${options.day}` +
    (options.cacheBust ? `&cb=${options.cacheBust}` : '')
  );
}

function dayString(offset: number, now: number): string {
  const date = new Date(now);
  date.setDate(date.getDate() + offset);
  return isoDay(date);
}

/**
 * Quel est le jour le plus récent réellement publié ?
 *
 * La NASA publie avec du retard : la nuit, la journée en cours n'existe pas
 * encore et le serveur répond un code 200 accompagné d'un message d'erreur au
 * lieu d'une image. On sonde donc avec une minuscule requête de 8 pixels sur
 * une emprise proche de Bordeaux, et on ne retient un jour que si le type de
 * contenu annonce bien une image.
 */
export async function findLatestAvailableDay(
  maxDaysBack = 3,
  now: number = Date.now(),
): Promise<number> {
  for (let offset = 0; offset >= -maxDaysBack; offset -= 1) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    try {
      const response = await fetch(
        tileUrl({
          bbox: '-200000,5450000,0,5650000',
          widthPx: 8,
          heightPx: 8,
          day: dayString(offset, now),
          layers: 'VIIRS_NOAA20_Thermal_Anomalies_375m_All',
        }),
        { signal: controller.signal, cache: 'no-store' },
      );
      const contentType = response.headers.get('content-type') ?? '';
      if (response.ok && contentType.includes('image')) return offset;
    } catch {
      // Ce jour-là ne répond pas : on essaie le précédent.
    } finally {
      clearTimeout(timer);
    }
  }
  return -1;
}

/**
 * Chargement d'image fiable.
 *
 * Par `fetch` plutôt que par une balise `<img crossOrigin>`, pour deux
 * raisons. Une balise échoue en bloc sans jamais dire pourquoi, alors qu'ici
 * on lit le code HTTP et le type de contenu, donc on sait si la NASA a
 * renvoyé un message d'erreur au lieu d'une image. Et une image décodée
 * depuis un blob local ne contamine pas le canvas, contrairement à un
 * chargement distant dont la réponse aurait été mise en cache sans en-tête
 * CORS par une visite précédente.
 */
async function loadImage(url: string, tries = 2): Promise<ImageBitmap | HTMLImageElement> {
  let lastError: Error | undefined;

  for (let attempt = 0; attempt < tries; attempt += 1) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), IMAGE_TIMEOUT_MS);
    try {
      // URL inchangée au premier essai : indispensable pour profiter du cache
      // de la NASA. Un paramètre unique systématique forcerait un rendu
      // serveur complet à chaque tuile, ce qui était la vraie cause de la
      // lenteur du balayage.
      const response = await fetch(attempt === 0 ? url : `${url}&n=${attempt}`, {
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const contentType = response.headers.get('content-type') ?? '';
      if (!contentType.includes('image')) {
        const text = (await response.text())
          .replace(/<[^>]*>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        throw new Error(`réponse "${contentType}" : ${text.slice(0, 80)}`);
      }

      const blob = await response.blob();
      if (blob.size === 0) throw new Error('image vide');

      if (typeof createImageBitmap === 'function') return await createImageBitmap(blob);

      // Repli : blob local, donc même origine, donc canvas non contaminé.
      const objectUrl = URL.createObjectURL(blob);
      try {
        return await new Promise<HTMLImageElement>((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = () => reject(new Error('décodage impossible'));
          image.src = objectUrl;
        });
      } finally {
        setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
      }
    } catch (error) {
      lastError =
        error instanceof Error && error.name === 'AbortError'
          ? new Error('délai dépassé')
          : error instanceof Error
            ? error
            : new Error(String(error));
      if (attempt < tries - 1) await delay(400);
    } finally {
      clearTimeout(timer);
    }
  }

  throw lastError ?? new Error('image illisible');
}

/**
 * Un seul canvas, réutilisé pour toutes les tuiles.
 *
 * En créer un par image réservait plus de cent mégaoctets, ce qui suffit à
 * faire échouer les décodages sur une machine chargée.
 */
let scratch: CanvasRenderingContext2D | null = null;

function scratchContext(tilePx: number): CanvasRenderingContext2D {
  if (!scratch) {
    const canvas = document.createElement('canvas');
    canvas.width = tilePx;
    canvas.height = tilePx;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error("Ce navigateur n'expose pas de canvas 2D.");
    scratch = context;
  }
  scratch.clearRect(0, 0, tilePx, tilePx);
  return scratch;
}

/** Relève les pixels chauds d'une tuile et les convertit en coordonnées. */
async function scanTile(
  box: TileBox,
  dayOffset: number,
  options: { cacheBust: number; tilePx: number; now: number },
): Promise<LatLon[]> {
  const image = await loadImage(
    tileUrl({
      bbox: toBboxParam(box),
      widthPx: options.tilePx,
      heightPx: options.tilePx,
      day: dayString(dayOffset, options.now),
      cacheBust: options.cacheBust,
    }),
  );

  const context = scratchContext(options.tilePx);
  context.drawImage(image, 0, 0, options.tilePx, options.tilePx);
  // Libère la mémoire de l'image aussitôt lue.
  if ('close' in image) image.close();

  /*
   * Une erreur ici doit REMONTER, jamais être avalée : sinon l'application
   * afficherait "aucun feu" alors qu'elle n'a simplement pas pu regarder.
   */
  const data = context.getImageData(0, 0, options.tilePx, options.tilePx).data;

  /*
   * L'image est presque entièrement transparente : les feux sont rares. On la
   * relit donc 32 bits à la fois. Un pixel totalement transparent vaut 0 quel
   * que soit l'ordre des octets de la machine, donc une seule comparaison
   * écarte 99,9 % des pixels, au lieu de quatre lectures de tableau chacun.
   * C'était ça, la lenteur : des millions de pixels examinés inutilement.
   */
  const words = new Uint32Array(data.buffer);
  const found: LatLon[] = [];

  for (let index = 0; index < words.length; index += 1) {
    if (words[index] === 0) continue;
    const byte = index << 2;
    if (isFirePixel(data[byte]!, data[byte + 1]!, data[byte + 2]!, data[byte + 3]!)) {
      found.push(
        pixelToLatLon(box, index % options.tilePx, (index / options.tilePx) | 0, options.tilePx),
      );
    }
  }

  return found;
}

export interface ScanProgress {
  points: readonly LatLon[];
  dayIndex: number;
  tilesDone: number;
  totalTiles: number;
  /** Vrai quand un jour entier vient d'être terminé : moment idéal pour redessiner. */
  dayComplete: boolean;
}

export interface ScanOptions {
  dayOffset: number;
  daysBack?: number;
  /** Incrémenté après un échec, pour contourner un cache dépourvu d'en-tête CORS. */
  cacheBust?: number;
  tilePx?: number;
  now?: number;
  onProgress?: (progress: ScanProgress) => void;
}

export interface ScanResult {
  points: LatLon[];
  report: ScanReport;
}

/**
 * Balayage en deux temps.
 *
 * D'abord le jour le plus récent sur toute la France, ce qui donne un premier
 * résultat affichable en quelques secondes, puis les jours précédents pour
 * compléter l'étendue des feux qui durent. Sans cela l'écran restait vide une
 * trentaine de secondes sans rien expliquer.
 */
export async function scanFrance(options: ScanOptions): Promise<ScanResult> {
  const tilePx = options.tilePx ?? SCAN_TILE_PX;
  const daysBack = options.daysBack ?? SCAN_DAYS_BACK;
  const cacheBust = options.cacheBust ?? 0;
  const now = options.now ?? Date.now();

  const boxes = franceTileGrid();
  const totalTiles = boxes.length * (daysBack + 1);

  const seen = new Set<string>();
  const points: LatLon[] = [];
  const absorb = (batch: readonly LatLon[]): void => {
    for (const point of batch) {
      // Grille d'environ un kilomètre : stable d'un balayage à l'autre.
      const key = `${point.lat.toFixed(2)},${point.lon.toFixed(2)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      points.push(point);
    }
  };

  let ok = 0;
  let failed = 0;
  let lastError: Error | null = null;

  for (let dayIndex = 0; dayIndex <= daysBack; dayIndex += 1) {
    let tilesDone = 0;
    await pool(boxes, SCAN_MAX_PARALLEL, async (box) => {
      try {
        absorb(await scanTile(box, options.dayOffset - dayIndex, { cacheBust, tilePx, now }));
        ok += 1;
      } catch (error) {
        failed += 1;
        lastError = error instanceof Error ? error : new Error(String(error));
      }
      tilesDone += 1;
      options.onProgress?.({
        points,
        dayIndex,
        tilesDone: dayIndex * boxes.length + tilesDone,
        totalTiles,
        dayComplete: false,
      });
    });

    options.onProgress?.({
      points,
      dayIndex,
      tilesDone: (dayIndex + 1) * boxes.length,
      totalTiles,
      dayComplete: true,
    });
  }

  return { points, report: { ok, failed, error: lastError } };
}
